import { PercentageEntry } from '../interfaces';

export const calculatePercentages: <T extends string>(values: Record<T, number>) => Record<T, number> = <T extends string>(
  values: Record<T, number>,
): Record<T, number> => {
  const valueEntries: [T, number][] = Object.entries(values) as Array<[T, number]>;

  const total: number = valueEntries.reduce(
    (sum: number, [, value]: [T, number]): number => sum + value,
    0,
  );

  if (total === 0) {
    return Object.fromEntries(
      valueEntries.map(([key]: [T, number]): [T, number] => [key, 0]),
    ) as Record<T, number>;
  }

  const entries: PercentageEntry<T>[] = valueEntries.map(([key, value]: [T, number]): PercentageEntry<T> => {
    const exact: number = (value / total) * 100;
    const roundedDown: number = Math.floor(exact);

    return {
      key,
      roundedDown,
      remainder: exact - roundedDown,
    };
  });

  let missing: number = 100 - entries.reduce((sum: number, entry: PercentageEntry<T>): number => sum + entry.roundedDown, 0);

  entries
    .sort((a: PercentageEntry<T>, b: PercentageEntry<T>): number => b.remainder - a.remainder)
    .forEach((entry: PercentageEntry<T>): void => {
      if (missing > 0) {
        entry.roundedDown += 1;
        missing -= 1;
      }
    });

  return Object.fromEntries(
    entries.map(({ key, roundedDown }: PercentageEntry<T>): [T, number] => [key, roundedDown]),
  ) as Record<T, number>;
};
